import { useEffect, useState } from 'react';
import { Alert, Button, Card, Select, Space, Table, Typography } from 'antd';
import { Link } from 'react-router-dom';
import PageScaffold from '../../components/PageScaffold';
import {
  VOIP_CALLS_PAGE_PATH,
  VOIP_CALLS_PAGE_TITLE,
} from '../leedflow/VoipCallsPage/VoipCallsPage';
import { E2ERunSummary } from '../../../domain/models/E2ERun';
import listE2ERuns, { ListE2ERunsPayload } from '../../../domain/use_cases/e2e/listE2ERuns';
import E2EStatus from './E2EStatus';
import useE2ERefresh from './useE2ERefresh';

export const E2E_RESULTS_PAGE_PATH = '/e2e-results';
export const E2E_RESULTS_PAGE_TITLE = 'Tests E2E';
export default function E2EResultsPage() {
  const [environment, setEnvironment] = useState('');
  const [conclusion, setConclusion] = useState('');
  const [cursor, setCursor] = useState('');
  const [state, setState] = useState<{
    key: string;
    runs: E2ERunSummary[];
    nextCursor: string | null;
    error: string;
  }>({ key: '', runs: [], nextCursor: null, error: '' });
  const [refresh, setRefresh] = useState(0);
  const key = JSON.stringify([environment, conclusion, cursor, refresh]);
  const loading = key !== state.key;
  const runs = loading ? [] : state.runs;
  const error = loading ? '' : state.error;
  const nextCursor = loading ? null : state.nextCursor;
  useEffect(() => {
    let cancelled = false;
    listE2ERuns(new ListE2ERunsPayload(environment, conclusion, cursor))
      .then((result) => {
        if (!cancelled) setState({ key, ...result, error: '' });
      })
      .catch((error) => {
        if (!cancelled)
          setState({
            key,
            runs: [],
            nextCursor: null,
            error: error.message ?? 'Chargement impossible',
          });
      });
    return () => {
      cancelled = true;
    };
  }, [environment, conclusion, cursor, key]);
  const active = runs.some(
    (run) =>
      ['pending', 'in_progress'].includes(run.apk.status) ||
      ['pending', 'in_progress'].includes(run.tests.status)
  );
  useE2ERefresh(active, refresh, setRefresh);
  return (
    <PageScaffold
      title={E2E_RESULTS_PAGE_TITLE}
      subtitle="Outil de debug dev : runs Android E2E beta/staging"
      breadcrumbItems={[
        { title: VOIP_CALLS_PAGE_TITLE, target: VOIP_CALLS_PAGE_PATH },
        { title: E2E_RESULTS_PAGE_TITLE },
      ]}
    >
      <Card
        title="Runs"
        extra={
          <Button onClick={() => setRefresh((v) => v + 1)} loading={loading}>
            Actualiser
          </Button>
        }
      >
        <Typography.Paragraph type="secondary">
          La préparation APK et la validation des parcours sont suivies séparément. Les contrôles
          techniques sont exclus des compteurs produit.
        </Typography.Paragraph>
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            aria-label="Environnement"
            style={{ width: 170 }}
            value={environment}
            onChange={(value) => {
              setEnvironment(value);
              setCursor('');
            }}
            options={[
              { value: '', label: 'Tous les environnements' },
              { value: 'beta', label: 'Beta' },
              { value: 'staging', label: 'Staging' },
            ]}
          />
          <Select
            aria-label="Résultat E2E"
            style={{ width: 180 }}
            value={conclusion}
            onChange={(value) => {
              setConclusion(value);
              setCursor('');
            }}
            options={[
              { value: '', label: 'Tous les résultats' },
              { value: 'success', label: 'Réussi' },
              { value: 'failure', label: 'Échec' },
              { value: 'unknown', label: 'Non vérifié' },
              { value: 'cancelled', label: 'Annulé' },
            ]}
          />
        </Space>
        {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
        <Table<E2ERunSummary>
          rowKey="runKey"
          dataSource={runs}
          loading={loading}
          pagination={false}
          scroll={{ x: 900 }}
          columns={[
            {
              title: 'Run',
              render: (_, run) => (
                <Link to={`${E2E_RESULTS_PAGE_PATH}/${encodeURIComponent(run.runKey)}`}>
                  #{run.runId} · tentative {run.latestAttempt}
                </Link>
              ),
            },
            {
              title: 'Source testée',
              render: (_, run) => (
                <>
                  <div>
                    {run.environment} · {run.branch}
                  </div>
                  <Typography.Text code>{run.commit.slice(0, 12)}</Typography.Text>
                </>
              ),
            },
            {
              title: 'APK',
              render: (_, run) => <E2EStatus status={run.apk.conclusion ?? run.apk.status} />,
            },
            {
              title: 'E2E',
              render: (_, run) => <E2EStatus status={run.tests.conclusion ?? run.tests.status} />,
            },
            { title: 'Rapport', render: (_, run) => <E2EStatus status={run.reportAvailability} /> },
            {
              title: 'Parcours produit',
              render: (_, run) =>
                `${run.counters.passed} réussis · ${run.counters.failed} échoués · ${run.counters.notRun} non exécutés`,
            },
            { title: 'Date', render: (_, run) => new Date(run.producedAt).toLocaleString() },
            {
              title: 'GitHub',
              render: (_, run) => (
                <a href={run.githubUrl} target="_blank" rel="noreferrer">
                  Ouvrir
                </a>
              ),
            },
          ]}
        />
        <Space style={{ marginTop: 16 }}>
          {cursor && <Button onClick={() => setCursor('')}>Première page</Button>}
          <Button
            disabled={!nextCursor || loading}
            onClick={() => {
              if (nextCursor) setCursor(nextCursor);
            }}
          >
            Page suivante
          </Button>
        </Space>
      </Card>
    </PageScaffold>
  );
}
